import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox, Receipt } from 'lucide-react';

import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { canImportReceiptManually } from '@/lib/referral/manualReceiptImport';
import { callFunction } from '@/lib/api';
import { referralBuildFailureMessage } from '@/lib/referral/referralBuildError';
import { MISSING_CLINICAL_REASON_MESSAGE, hasClinicalReason } from '@/lib/referral/xref01Validation';
import { Banner } from '@/components/ui/Banner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Textarea';
import { ResponsiveTable } from '@/components/ui/Table';
import { CtlReceiptExplanation } from '@/components/medcom/CtlReceiptExplanation';
import { downloadMedcomDocument } from '@/lib/medcom/encodeMedcomDocument';
import {
  ctlReceiptDownloadFilename,
  ctlRefuseCodeFromXml,
  ctlRefuseTextsFromXml,
  isSentCtlReceipt,
} from '@/lib/medcom/ctlReceiptText';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { CTL_PREVIEW_DEFAULT_LIMIT } from '@/lib/medcom/ctlPreviewQuery';
import { ReceivedCorrespondencePanel } from '@/components/medcom/ReceivedCorrespondencePanel';
import { ReceivedXdis16Panel } from '@/components/xdis/ReceivedXdis16Panel';
import { ReceivedBookingsvarPanel } from '@/components/medcom/ReceivedBookingsvarPanel';
import { ReceivedGgopPanel } from '@/components/ggop/ReceivedGgopPanel';
import { ReceivedEpikriserPanel } from '@/components/medcom/ReceivedEpikriserPanel';
import { ReceivedLabReportsPanel } from '@/components/medcom/ReceivedLabReportsPanel';
import { LabPartiesPanel } from '@/components/lab/LabPartiesPanel';
import { LabAnalysisCataloguePanel } from '@/components/lab/LabAnalysisCataloguePanel';
import { SupExportPanel } from '@/components/sup/SupExportPanel';
import { formatDanishDateTime } from '@/lib/danishDateTime';

const STANDARDS = [
  { id: 'BIN01', label: 'BIN01', ready: true },
  { id: 'CTL', label: 'CTL', ready: true },
  { id: 'DIS01', label: 'DIS01', ready: true },
  { id: 'DIS02', label: 'DIS02', ready: true },
  { id: 'DIS03', label: 'DIS03', ready: true },
  { id: 'DIS05', label: 'DIS05', ready: true },
  { id: 'DIS13', label: 'DIS13', ready: true },
  { id: 'GGOP', label: 'GGOP', ready: true },
  { id: 'REF01', label: 'REF01', ready: true },
  { id: 'REF02', label: 'REF02', ready: true },
  { id: 'REF06', label: 'REF06', ready: true },
  { id: 'RPT01', label: 'RPT01 / XRPT01', ready: true },
  { id: 'SUP', label: 'SUP', ready: true },
  { id: 'XDIS', label: 'XDIS', ready: true },
  { id: 'XRPT05', label: 'XRPT05', ready: true },
  { id: 'VANS', label: 'VANS', ready: true },
] as const;

type StandardId = (typeof STANDARDS)[number]['id'];

const REF_STANDARD_IDS = new Set<StandardId>(['REF01', 'REF02', 'REF06']);

/**
 * Tabs that show the admin import panel and the exchange log (Spor A C3), so a
 * receive-side screenshot shows the standard under test. BIN01 and VANS have
 * no builder on this page; they are import-only here.
 */
const IMPORT_STANDARD_IDS = new Set<StandardId>(['CTL', 'REF01', 'REF06', 'BIN01', 'VANS', 'DIS01', 'DIS02', 'DIS03', 'DIS05', 'DIS13', 'GGOP', 'XDIS', 'RPT01', 'XRPT05']);
const IMPORT_ONLY_STANDARD_IDS = new Set<StandardId>(['BIN01', 'VANS', 'DIS01', 'DIS02', 'DIS03', 'DIS05', 'DIS13', 'GGOP', 'XDIS', 'RPT01', 'XRPT05']);
/** T1.4: receive tabs for the epikriser; 'Dan epikrise' (send) lives on the received referral. */
const EPIKRISE_STANDARD_KIND: Partial<Record<StandardId, string>> = { DIS01: 'XDIS01', DIS02: 'XDIS02', DIS03: 'XDIS03', DIS05: 'XDIS05' };

type Kind = 'XCTL01' | 'XCTL02' | 'XCTL03';

// ── REF types ─────────────────────────────────────────────────────────────────

type RefKind = 'XREF01' | 'XREF02' | 'XREF06';

const REF_KINDS = [
  { id: 'XREF01' as RefKind, label: 'XREF01 — Hospitalshenvisning' },
  { id: 'XREF02' as RefKind, label: 'XREF02 — Røntgenhenvisning' },
  { id: 'XREF06' as RefKind, label: 'XREF06 — Speciallægehenvisning' },
] as const;

const XREF06_DIAGNOSE_CODES = ['1', '3', '4', '5', '6'] as const;

type RefKindDefaults = {
  acknowledgementCode: string;
  senderEan: string;
  senderIdentifier: string;
  senderIdentifierCode: string;
  senderOrganisationName: string;
  senderMedicalSpecialityCode: string;
  referrerPersonName: string;
  referrerPersonTitle: string;
  receiverEan: string;
  receiverIdentifier: string;
  receiverIdentifierCode: string;
  patientCpr: string;
  patientSurname: string;
  patientGivenName: string;
  episodeOfCareType: string;
  episodeOfCareId: string;
  episodeOfCareIdentifier: string;
  diagnoseDescriptionCode: string;
  diagnoseCode: string;
  diagnoseTypeCode: string;
  diagnoseText: string;
  anamnesis: string;
  clinicalReason: string;
};

/**
 * Prefill values drawn from MedCom example files:
 *   XREF01 → Spor A/Referencer/REF01/XML/Eksempler/XREF01_eks.xml
 *   XREF02 → Spor A/Referencer/REF02/XML/Eksempler/XREF02_eks.xml
 *   XREF06 → Spor A/Referencer/REF06/XML/Eksempler/XREF06_eks.xml
 */
const REF_DEFAULTS: Record<RefKind, RefKindDefaults> = {
  XREF01: {
    acknowledgementCode: 'pluspositivkvitt',
    senderEan: '5790000121526',
    senderIdentifier: '079741',
    senderIdentifierCode: 'ydernummer',
    senderOrganisationName: 'Lægehuset',
    senderMedicalSpecialityCode: 'almenlaege_laegevagt',
    referrerPersonName: 'Hans Jensen',
    referrerPersonTitle: 'Uddannelsesamanuensis',
    receiverEan: '5790000201389',
    receiverIdentifier: '2001060',
    receiverIdentifierCode: 'sygehusafdelingsnummer',
    patientCpr: '2509479989',
    patientSurname: 'Elmer',
    patientGivenName: 'Bruno Test',
    episodeOfCareType: 'henviser',
    episodeOfCareId: '6c8057f4caf24479ac300720181437',
    episodeOfCareIdentifier: '',
    diagnoseDescriptionCode: 'henv_diagnose',
    diagnoseCode: 'DI10',
    diagnoseTypeCode: 'SKSdiagnosekode',
    diagnoseText: 'Blodtryksforhøjelse af ukendt årsag.',
    anamnesis:
      '1995 indlagt Køge Sygehus under mistanke om AMI, afkræftedes. Januar 98 indlagt Frederiksborg Sgh., med afd., med stenokardi.',
    clinicalReason: '',
  },
  XREF02: {
    acknowledgementCode: 'minuspositivkvitt',
    senderEan: '5790000127269',
    senderIdentifier: '50823',
    senderIdentifierCode: 'ydernummer',
    senderOrganisationName: 'Lægehuset',
    senderMedicalSpecialityCode: 'almenlaege_laegevagt',
    referrerPersonName: 'Arne Vestergaard',
    referrerPersonTitle: 'Læge',
    receiverEan: '5790000183784',
    receiverIdentifier: '2001060',
    receiverIdentifierCode: 'sygehusafdelingsnummer',
    patientCpr: '1509819996',
    patientSurname: 'Berggren',
    patientGivenName: 'Brita',
    episodeOfCareType: 'henviser',
    episodeOfCareId: '1b0bbc344499442894a922300720181600',
    episodeOfCareIdentifier: '',
    diagnoseDescriptionCode: '',
    diagnoseCode: '',
    diagnoseTypeCode: '',
    diagnoseText: '',
    anamnesis:
      'Patienten har kendt kronisk obstruktiv lungesygdom og er senest behandlet for pneumoni under indlæggelse på medicinsk afdeling.',
    clinicalReason: '',
  },
  XREF06: {
    acknowledgementCode: 'pluspositivkvitt',
    senderEan: '5790000127269',
    senderIdentifier: '012345',
    senderIdentifierCode: 'ydernummer',
    senderOrganisationName: 'Lægehuset',
    senderMedicalSpecialityCode: 'almenlaege_laegevagt',
    referrerPersonName: 'Hans Andersen',
    referrerPersonTitle: 'Læge',
    receiverEan: '5790000183784',
    receiverIdentifier: '078901',
    receiverIdentifierCode: 'ydernummer',
    patientCpr: '0201609996',
    patientSurname: 'Lauridsen',
    patientGivenName: 'Lonni Test',
    episodeOfCareType: '',
    episodeOfCareId: '',
    episodeOfCareIdentifier: '47f288ede4154b39bfd81bc9be6d6c7e',
    diagnoseDescriptionCode: '',
    diagnoseCode: '1',
    diagnoseTypeCode: 'speciallaegehenvisning',
    diagnoseText: 'Udredning og behandling',
    anamnesis: 'Pityriasis rosae obs.pro.',
    clinicalReason: '',
  },
};

type RefPreview = {
  consumerName: string;
  id: string;
  ok: boolean;
  kind: RefKind;
  xml: string;
  checks: Check[];
};

const KINDS = [
  { id: 'XCTL01', label: 'XCTL01 — Negativ VANS-kvittering' },
  { id: 'XCTL02', label: 'XCTL02 — Negativ kvittering' },
  { id: 'XCTL03', label: 'XCTL03 — Positiv kvittering' },
] as const;

const REFUSE_CODES = [
  'ikke_specificeret',
  'ukendt_lokationsnummer',
  'problem_med_modtagerID',
  'problem_med_version',
  'syntaksfejl',
] as const;

/** Empty means send no RefuseCode at all, which the facitliste allows. */
type RefuseCode = (typeof REFUSE_CODES)[number] | '';

const MAX_ORIGINAL_LETTERS = 99;

/**
 * Decode a MedCom XML file using the encoding it declares.
 *
 * MedCom requires ISO-8859-1 (CTL-Send 4.2), and every example file is written in
 * it. `File.text()` always decodes UTF-8, which turns the Danish letters in real
 * addresses into replacement characters — "Lægehuset" becomes "L?gehuset" — so the
 * declaration has to be honoured instead.
 *
 * The declaration is read from the first bytes as Latin-1, which is safe because
 * the declaration itself is ASCII in any encoding we accept here.
 */
function decodeMedcomXml(bytes: ArrayBuffer): string {
  const head = new TextDecoder('iso-8859-1').decode(bytes.slice(0, 200));
  const declared = /encoding=["']([^"']+)["']/i.exec(head)?.[1]?.toLowerCase();
  const utf8Bom = new Uint8Array(bytes.slice(0, 3));
  const hasUtf8Bom = utf8Bom[0] === 0xef && utf8Bom[1] === 0xbb && utf8Bom[2] === 0xbf;
  const encoding = hasUtf8Bom || declared === undefined || declared.startsWith('utf')
    ? 'utf-8'
    : declared;
  try {
    return new TextDecoder(encoding).decode(bytes);
  } catch {
    // An encoding the browser does not know. Latin-1 never throws and preserves
    // the bytes one-to-one, which is closer to the truth than a UTF-8 guess.
    return new TextDecoder('iso-8859-1').decode(bytes);
  }
}

/** RPT01 EDIFACT is UNOC / ISO-8859-1. Do not run the XML declaration guess. */
function decodeMedcomImportFile(bytes: ArrayBuffer, fileName?: string | null): string {
  const latin1 = new TextDecoder('iso-8859-1').decode(bytes);
  const head = latin1.trimStart();
  const name = (fileName ?? '').toLowerCase();
  if (name.endsWith('.edi') || head.startsWith('UNA') || head.startsWith('UNB')) {
    return latin1;
  }
  return decodeMedcomXml(bytes);
}

/**
 * A fresh forløbsID — REF01-Send 3.9, "At der dannes et unikt UUID ver. 4".
 *
 * ref-preview mints one when the field is left empty. This exists so the value is
 * visible on screen before the document is built, which is what the step asks to
 * be shown. Hyphens are stripped to match every one of MedCom's 32 example values.
 */
function newEpisodeIdentifier(): string {
  return crypto.randomUUID().replace(/-/g, '');
}

type OriginalLetter = { identifier: string; versionCode: string };

type Check = { id: string; pass: boolean; detail: string };

type CtlPreview = {
  consumerName: string;
  id: string;
  ok: boolean;
  kind: Kind;
  bodyName: string;
  versionCode: string;
  xml: string;
  checks: Check[];
};

// ── Exchange log ────────────────────────────────────────────────────────────

type ExchangeRow = {
  id: string;
  standard: string;
  kind: string;
  direction: 'in' | 'out';
  envelopeIdentifier: string | null;
  letterIdentifier: string | null;
  senderEan: string | null;
  receiverEan: string | null;
  originalEnvelopeIdentifier: string | null;
  originalLetterIdentifier: string | null;
  refuseCode: string | null;
  refuseReason?: string | null;
  refuseText?: string | null;
  validationOk: boolean;
  checks: Check[];
  importedAt: string | null;
  createdAt: string;
};

type ListOutput = {
  consumerName: string;
  exchanges: ExchangeRow[];
  hasMore?: boolean;
  total?: number;
  limit?: number;
  offset?: number;
};

const EXCHANGE_KIND_OPTIONS = [
  '',
  'XREF01',
  'XREF02',
  'XREF06',
  'XDIS91',
  'XBIN01',
  'XBIN02',
  'XCTL01',
  'XCTL02',
  'XCTL03',
  'XRPT01',
  'XRPT05',
] as const;

// ── Import result ────────────────────────────────────────────────────────────

/** Returned when an XCONTRL receipt is imported (CTL-Receive 1.3). */
type ReceiptImportResult = {
  consumerName: string;
  id: string;
  kind: string;
  envelopeIdentifier: string;
  letterIdentifier: string;
  senderEan: string;
  receiverEan: string;
  originalEnvelopeIdentifier: string | null;
  refuseCode: string | null;
  refuseText: string | null;
  validationOk: boolean;
  missingMandatory: string[];
  /** CTL-Send 3.24: file loaded a second time. */
  doublet: boolean;
  /** CTL-Receive 3.4, 3.8: links this import to a previously built receipt. */
  coupledToId: string | null;
  /** C19: PositiveMessage when the receipt came in a VANSEnvelope (not for unreliable). */
  vansReceipt?: VansReceiptOut | null;
  vansReceiptNote?: string | null;
};

/** VANS receipt Clinio generated for an imported VANSEnvelope (C19, C21). UTF-8. */
type VansReceiptOut = {
  id: string;
  kind: string;
  envelopeIdentifier: string;
  originalEnvelopeIdentifier: string | null;
  errorText: string | null;
  xml: string;
};

/** C17: an imported VANS Receipt, coupled to the envelope it answers. */
type VansReceiptImportResult = {
  consumerName: string;
  importKind: 'vans-receipt';
  id: string;
  receiptKind: string;
  envelopeIdentifier: string;
  originalEnvelopeIdentifier: string | null;
  coupledToId: string | null;
  matchesSent: boolean;
  errorCode: string | null;
  errorText: string | null;
  checks: { id: string; pass: boolean; detail: string }[];
};

/** C21: a VANSEnvelope that could not be unpacked, answered with a negative VANS receipt. */
type VansRejectedResult = {
  consumerName: string;
  importKind: 'vans-rejected';
  id: string;
  errorCode: string;
  errorText: string;
  vansReceipt: VansReceiptOut | null;
};

/** Returned when a MedCom message (XREF01, XREF02, XBIN01, …) is imported (CTL-Send 2.1–2.4). */
type MessageImportResult = {
  consumerName: string;
  importKind: 'message';
  id: string;
  typeCode: string;
  envelopeIdentifier: string;
  letterIdentifier: string;
  versionCode: string;
  senderEan: string;
  receiverEan: string;
  acknowledgementCode: string;
  refuseCode: string | null;
  refuseReason: string | null;
  generatedReceiptKind: 'XCTL02' | 'XCTL03' | 'CTL02' | 'CTL03' | null;
  generatedReceiptFormat?: 'XML' | 'EDIFACT' | null;
  generatedReceiptId: string | null;
  generatedReceiptXml: string | null;
  generatedReceiptVansXml: string | null;
  inboundVansWrapped: boolean;
  doublet: boolean;
  receiptForMessageId: string | null;
  vansReceipt?: VansReceiptOut | null;
  vansReceiptNote?: string | null;
};

type ImportResult = ReceiptImportResult | MessageImportResult | VansReceiptImportResult | VansRejectedResult;

/** C19/C21: download the generated VANS receipt, or say why there is none. */
function VansReceiptBlock({ receipt, note }: { receipt?: VansReceiptOut | null; note?: string | null }) {
  if (receipt) {
    return (
      <div className="mt-3">
        <Button
          type="button"
          onClick={() => downloadMedcomDocument(`VANS_${receipt.kind}_${receipt.envelopeIdentifier}.xml`, receipt.xml)}
        >
          Hent VANS-kvittering ({receipt.kind})
        </Button>
        <p className="mt-1 text-xs text-ds-muted">
          Ny EnvelopeIdentifier {receipt.envelopeIdentifier}
          {receipt.originalEnvelopeIdentifier && <> · OriginalEnvelopeIdentifier {receipt.originalEnvelopeIdentifier}</>}
        </p>
      </div>
    );
  }
  if (note) return <p className="mt-3 text-sm text-ds-muted">{note}</p>;
  return null;
}

// ────────────────────────────────────────────────────────────────────────────

/** Indentation is kept: RefuseText is shown in a fixed-width 70-column field, so it carries layout. */
function toLines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.replace(/\r$/, ''))
    .filter((line) => line.trim().length > 0);
}

function formatTs(iso: string): string {
  return formatDanishDateTime(iso);
}

function LoadedImportFileName({
  filename,
  className,
}: {
  filename: string | null;
  className?: string;
}) {
  const { t } = useTranslation();
  if (!filename) return null;
  return <p className={cn('text-sm text-ds-text', className)}>{t('medcomSection.loaded_file', { filename })}</p>;
}

function ExchangeLogNotes({
  row,
  doubletKeys,
  outByEnvelope,
  effectiveAckByOriginalEnvelope,
}: {
  row: ExchangeRow;
  doubletKeys: Set<string>;
  outByEnvelope: Map<string, string>;
  effectiveAckByOriginalEnvelope: Map<string, 'positiv' | 'negativ'>;
}) {
  const doubletKey =
    row.direction === 'in' && row.envelopeIdentifier && row.letterIdentifier
      ? `${row.envelopeIdentifier}|${row.letterIdentifier}`
      : null;
  const isDoublet = doubletKey ? doubletKeys.has(doubletKey) : false;

  // CTL-Receive 3.4, 3.8: coupling — an in-row whose original_envelope_identifier
  // matches the envelope_identifier of a known out-row.
  const coupledOutId =
    row.direction === 'in' && row.originalEnvelopeIdentifier
      ? (outByEnvelope.get(row.originalEnvelopeIdentifier) ?? null)
      : null;

  // CTL-Receive 2.4: effective ack for this row.
  // Out-rows: keyed by their own envelope_identifier (they are the original message).
  // In-rows (XCTL02/XCTL03): keyed by original_envelope_identifier (the message they answer).
  const effectiveAck =
    row.direction === 'out' && row.envelopeIdentifier
      ? (effectiveAckByOriginalEnvelope.get(row.envelopeIdentifier) ?? null)
      : row.direction === 'in' &&
          row.originalEnvelopeIdentifier &&
          (row.kind === 'XCTL02' || row.kind === 'XCTL03')
        ? (effectiveAckByOriginalEnvelope.get(row.originalEnvelopeIdentifier) ?? null)
        : null;

  return (
    <span className="space-y-0.5">
      {/* CTL-Send 3.24: doublet flag */}
      {isDoublet ? (
        <span className="block font-medium text-ds-amber-ink">Dobbeltindlæsning</span>
      ) : null}
      {/* CTL-Receive 3.4, 3.8: coupling */}
      {coupledOutId ? (
        <span className="block text-ds-green-ink">Knyttet til afsendt</span>
      ) : null}
      {/* CTL-Receive 2.4 / 3.17–3.19: effective acknowledgement state.
          Negative (XCTL02) takes precedence regardless of import order.
          Shown on the original out-row and on each XCTL02/XCTL03 in-row. */}
      {effectiveAck ? (
        <span
          className={`block font-medium ${
            effectiveAck === 'negativ' ? 'text-ds-red-ink' : 'text-ds-green-ink'
          }`}
        >
          {row.direction === 'out'
            ? `Kvitteret: ${effectiveAck === 'negativ' ? 'Negativ' : 'Positiv'}`
            : `Effektiv tilstand: ${effectiveAck === 'negativ' ? 'Negativ' : 'Positiv'}`}
        </span>
      ) : null}
    </span>
  );
}

export function Standarder(_props: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isPlatformAdmin, isClinicAdmin, hasPermission } = useAuth();
  // Manual import is admin-only; communicate-ctl enforces the same rule (403).
  const canImport = canImportReceiptManually({
    isPlatformAdmin,
    isClinicAdmin: activeClinicId ? isClinicAdmin(activeClinicId) : false,
  });
  const canExportSup = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'medcom_sup', 'view'),
  );

  // ── Standard selector ─────────────────────────────────────────────────────
  const [standard, setStandard] = useState<StandardId>('CTL');

  // ── Build form ────────────────────────────────────────────────────────────
  // XCTL03 first: CTL-Send covers only XCTL02 and XCTL03, and the positive receipt is the common case.
  const [kind, setKind] = useState<Kind>('XCTL03');
  const [senderEan, setSenderEan] = useState('');
  const [receiverEan, setReceiverEan] = useState('');
  const [originalEnvelopeIdentifier, setOriginalEnvelopeIdentifier] = useState('');
  const [originalSenderEan, setOriginalSenderEan] = useState('');
  const [originalReceiverEan, setOriginalReceiverEan] = useState('');
  const [envelopeIdentifier, setEnvelopeIdentifier] = useState('');
  const [letterIdentifier, setLetterIdentifier] = useState('');
  const [sentDate, setSentDate] = useState('');
  const [sentTime, setSentTime] = useState('');
  const [refuseCode, setRefuseCode] = useState<RefuseCode>('ikke_specificeret');
  const [refuseText, setRefuseText] = useState('');
  const [originalLetters, setOriginalLetters] = useState<OriginalLetter[]>([]);
  const [preview, setPreview] = useState<CtlPreview | null>(null);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [buildBusy, setBuildBusy] = useState(false);

  const negative = kind === 'XCTL01' || kind === 'XCTL02';

  // ── Import panel ──────────────────────────────────────────────────────────
  const [importXml, setImportXml] = useState('');
  const [importFileName, setImportFileName] = useState<string | null>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [correspondenceRefresh, setCorrespondenceRefresh] = useState(0);
  const [importBusy, setImportBusy] = useState(false);

  // ── REF form ───────────────────────────────────────────────────────────────
  const [refKind, setRefKind] = useState<RefKind>('XREF01');
  const [refAcknowledgementCode, setRefAcknowledgementCode] = useState(REF_DEFAULTS.XREF01.acknowledgementCode);
  const [refSenderEan, setRefSenderEan] = useState(REF_DEFAULTS.XREF01.senderEan);
  const [refSenderIdentifier, setRefSenderIdentifier] = useState(REF_DEFAULTS.XREF01.senderIdentifier);
  const [refSenderIdentifierCode, setRefSenderIdentifierCode] = useState(REF_DEFAULTS.XREF01.senderIdentifierCode);
  const [refSenderOrganisationName, setRefSenderOrganisationName] = useState(REF_DEFAULTS.XREF01.senderOrganisationName);
  const [refSenderMedicalSpecialityCode, setRefSenderMedicalSpecialityCode] = useState(REF_DEFAULTS.XREF01.senderMedicalSpecialityCode);
  const [refReferrerPersonName, setRefReferrerPersonName] = useState(REF_DEFAULTS.XREF01.referrerPersonName);
  const [refReferrerPersonTitle, setRefReferrerPersonTitle] = useState(REF_DEFAULTS.XREF01.referrerPersonTitle);
  const [refReceiverEan, setRefReceiverEan] = useState(REF_DEFAULTS.XREF01.receiverEan);
  const [refReceiverIdentifier, setRefReceiverIdentifier] = useState(REF_DEFAULTS.XREF01.receiverIdentifier);
  const [refReceiverIdentifierCode, setRefReceiverIdentifierCode] = useState(REF_DEFAULTS.XREF01.receiverIdentifierCode);
  const [refPatientCpr, setRefPatientCpr] = useState(REF_DEFAULTS.XREF01.patientCpr);
  const [refPatientSurname, setRefPatientSurname] = useState(REF_DEFAULTS.XREF01.patientSurname);
  const [refPatientGivenName, setRefPatientGivenName] = useState(REF_DEFAULTS.XREF01.patientGivenName);
  const [refEpisodeOfCareType, setRefEpisodeOfCareType] = useState(REF_DEFAULTS.XREF01.episodeOfCareType);
  const [refEpisodeOfCareId, setRefEpisodeOfCareId] = useState(REF_DEFAULTS.XREF01.episodeOfCareId);
  const [refEpisodeOfCareIdentifier, setRefEpisodeOfCareIdentifier] = useState(REF_DEFAULTS.XREF01.episodeOfCareIdentifier);
  const [refDiagnoseDescriptionCode, setRefDiagnoseDescriptionCode] = useState(REF_DEFAULTS.XREF01.diagnoseDescriptionCode);
  const [refDiagnoseCode, setRefDiagnoseCode] = useState(REF_DEFAULTS.XREF01.diagnoseCode);
  const [refDiagnoseTypeCode, setRefDiagnoseTypeCode] = useState(REF_DEFAULTS.XREF01.diagnoseTypeCode);
  const [refDiagnoseText, setRefDiagnoseText] = useState(REF_DEFAULTS.XREF01.diagnoseText);
  const [refAnamnesis, setRefAnamnesis] = useState(REF_DEFAULTS.XREF01.anamnesis);
  const [refClinicalReason, setRefClinicalReason] = useState(REF_DEFAULTS.XREF01.clinicalReason);
  const [refPreview, setRefPreview] = useState<RefPreview | null>(null);
  const [refError, setRefError] = useState<string | null>(null);
  const [refBusy, setRefBusy] = useState(false);

  // ── Exchange log ──────────────────────────────────────────────────────────
  const [exchanges, setExchanges] = useState<ExchangeRow[]>([]);
  const [logError, setLogError] = useState<string | null>(null);
  const [logBusy, setLogBusy] = useState(false);
  const [logDraftSearch, setLogDraftSearch] = useState('');
  const [logDraftKind, setLogDraftKind] = useState('');
  const [logSearch, setLogSearch] = useState('');
  const [logKind, setLogKind] = useState('');
  const [logOffset, setLogOffset] = useState(0);
  const [logHasMore, setLogHasMore] = useState(false);
  const logFirstIdRef = useRef<string | null>(null);
  const [receiptDetail, setReceiptDetail] = useState<ExchangeRow | null>(null);
  const [receiptXml, setReceiptXml] = useState<string | null>(null);
  const [receiptBusy, setReceiptBusy] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);

  const loadLog = useCallback(async () => {
    if (!activeClinicId) return;
    setLogBusy(true);
    setLogError(null);
    try {
      const result = await callFunction<Record<string, unknown>, ListOutput>('communicate-ctl', {
        action: 'preview',
        clinicId: activeClinicId,
        limit: CTL_PREVIEW_DEFAULT_LIMIT,
        offset: logOffset,
        search: logSearch.trim() || undefined,
        kind: logKind.trim() || undefined,
      });
      const next = result.exchanges;
      const samePage = logOffset > 0 && Boolean(next[0]?.id) && logFirstIdRef.current === next[0]?.id;
      const fromTotal = typeof result.total === 'number'
        ? logOffset + next.length < result.total
        : result.hasMore === true;
      setLogHasMore(fromTotal && !samePage);
      logFirstIdRef.current = next[0]?.id ?? null;
      setExchanges(next);
    } catch (err) {
      setLogError(err instanceof Error ? err.message : 'Kunne ikke hente loggen.');
    } finally {
      setLogBusy(false);
    }
  }, [activeClinicId, logOffset, logSearch, logKind]);

  const fetchReceiptXml = useCallback(
    async (row: ExchangeRow): Promise<string> => {
      if (!activeClinicId) {
        throw new Error(t('medcomSection.exchange_receipt_need_clinic'));
      }
      const result = await callFunction<Record<string, unknown>, { xml: string }>(
        'communicate-ctl',
        {
          action: 'payload',
          clinicId: activeClinicId,
          id: row.id,
        },
      );
      return result.xml;
    },
    [activeClinicId, t],
  );

  const openReceiptDetail = useCallback(
    async (row: ExchangeRow) => {
      setReceiptDetail(row);
      setReceiptXml(null);
      setReceiptError(null);
      setReceiptBusy(true);
      try {
        setReceiptXml(await fetchReceiptXml(row));
      } catch (err) {
        setReceiptError(
          err instanceof Error ? err.message : t('medcomSection.exchange_receipt_load_error'),
        );
      } finally {
        setReceiptBusy(false);
      }
    },
    [fetchReceiptXml, t],
  );

  const downloadReceipt = useCallback(
    async (row: ExchangeRow, xml?: string | null) => {
      setReceiptError(null);
      try {
        const payload = xml ?? (await fetchReceiptXml(row));
        downloadMedcomDocument(
          ctlReceiptDownloadFilename(row.kind, row.envelopeIdentifier, row.id),
          payload,
        );
      } catch (err) {
        setReceiptError(
          err instanceof Error ? err.message : t('medcomSection.exchange_receipt_load_error'),
        );
      }
    },
    [fetchReceiptXml, t],
  );

  // Load the log on tabs with the import panel (receipt visibility) once the clinic is known.
  useEffect(() => {
    if (IMPORT_STANDARD_IDS.has(standard) && activeClinicId) {
      void loadLog();
    }
  }, [standard, activeClinicId, loadLog]);

  // ── Build handler ─────────────────────────────────────────────────────────
  const onCheck = async () => {
    setBuildError(null);
    setPreview(null);
    if (!activeClinicId) {
      setBuildError('Vælg en klinik.');
      return;
    }
    const letters = originalLetters.filter(
      (letter) => letter.identifier.trim().length > 0 || letter.versionCode.trim().length > 0,
    );
    const lines = toLines(refuseText);
    setBuildBusy(true);
    try {
      const result = await callFunction<Record<string, unknown>, CtlPreview>('communicate-ctl', {
        clinicId: activeClinicId,
        kind,
        senderEan,
        receiverEan,
        originalEnvelopeIdentifier,
        originalSenderEan,
        originalReceiverEan,
        ...(envelopeIdentifier ? { envelopeIdentifier } : {}),
        ...(letterIdentifier ? { letterIdentifier } : {}),
        ...(sentDate ? { sentDate } : {}),
        ...(sentTime ? { sentTime } : {}),
        ...(negative && refuseCode ? { refuseCode } : {}),
        ...(negative && lines.length > 0 ? { refuseText: lines } : {}),
        ...(letters.length > 0 ? { originalLetters: letters } : {}),
      });
      setPreview(result);
      void loadLog();
    } catch (err) {
      setBuildError(err instanceof Error ? err.message : 'Kunne ikke bygge kvitteringen.');
    } finally {
      setBuildBusy(false);
    }
  };

  // ── Import handler ────────────────────────────────────────────────────────
  const onImport = async () => {
    setImportError(null);
    setImportResult(null);
    if (!activeClinicId) {
      setImportError('Vælg en klinik.');
      return;
    }
    const xml = importXml.trim();
    if (!xml) {
      setImportError('Indsæt eller indlæs en XML- eller EDI-fil.');
      return;
    }
    setImportBusy(true);
    try {
      const result = await callFunction<Record<string, unknown>, ImportResult>('communicate-ctl', {
        action: 'import',
        clinicId: activeClinicId,
        xml,
        format: xml.startsWith('UNA') || xml.startsWith('UNB') ? 'EDIFACT' : 'XML',
      });
      setImportResult(result);
      void loadLog();
      // C10: a just imported XDIS91/XBIN01 shows in 'Modtagne korrespondancer' at once.
      setCorrespondenceRefresh((current) => current + 1);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Kunne ikke importere kvitteringen.');
    } finally {
      setImportBusy(false);
    }
  };

  // ── REF helpers ───────────────────────────────────────────────────────────

  function applyRefDefaults(k: RefKind) {
    const d = REF_DEFAULTS[k];
    setRefKind(k);
    setRefAcknowledgementCode(d.acknowledgementCode);
    setRefSenderEan(d.senderEan);
    setRefSenderIdentifier(d.senderIdentifier);
    setRefSenderIdentifierCode(d.senderIdentifierCode);
    setRefSenderOrganisationName(d.senderOrganisationName);
    setRefSenderMedicalSpecialityCode(d.senderMedicalSpecialityCode);
    setRefReferrerPersonName(d.referrerPersonName);
    setRefReferrerPersonTitle(d.referrerPersonTitle);
    setRefReceiverEan(d.receiverEan);
    setRefReceiverIdentifier(d.receiverIdentifier);
    setRefReceiverIdentifierCode(d.receiverIdentifierCode);
    setRefPatientCpr(d.patientCpr);
    setRefPatientSurname(d.patientSurname);
    setRefPatientGivenName(d.patientGivenName);
    setRefEpisodeOfCareType(d.episodeOfCareType);
    setRefEpisodeOfCareId(d.episodeOfCareId);
    setRefEpisodeOfCareIdentifier(d.episodeOfCareIdentifier);
    setRefDiagnoseDescriptionCode(d.diagnoseDescriptionCode);
    setRefDiagnoseCode(d.diagnoseCode);
    setRefDiagnoseTypeCode(d.diagnoseTypeCode);
    setRefDiagnoseText(d.diagnoseText);
    setRefAnamnesis(d.anamnesis);
    setRefClinicalReason(d.clinicalReason);
    setRefPreview(null);
    setRefError(null);
  }

  const onRefCheck = async () => {
    setRefError(null);
    setRefPreview(null);
    if (!activeClinicId) {
      setRefError('Vælg en klinik.');
      return;
    }
    if (refKind !== 'XREF06' && !hasClinicalReason(refClinicalReason)) {
      setRefError(MISSING_CLINICAL_REASON_MESSAGE);
      return;
    }
    const anamnesisLines = toLines(refAnamnesis);
    const hasEpisodeOfCare = refKind !== 'XREF06' && refEpisodeOfCareId.trim().length > 0;
    const hasEpisodeOfCareIdentifier = refKind === 'XREF06' && refEpisodeOfCareIdentifier.trim().length > 0;
    const hasReferral =
      refDiagnoseCode.trim().length > 0 ||
      refDiagnoseTypeCode.trim().length > 0 ||
      refDiagnoseText.trim().length > 0;
    setRefBusy(true);
    try {
      const result = await callFunction<Record<string, unknown>, RefPreview>('communicate-ref', {
        clinicId: activeClinicId,
        kind: refKind,
        ...(refAcknowledgementCode ? { acknowledgementCode: refAcknowledgementCode } : {}),
        sender: {
          ean: refSenderEan,
          ...(refSenderIdentifier ? { identifier: refSenderIdentifier } : {}),
          ...(refSenderIdentifierCode ? { identifierCode: refSenderIdentifierCode } : {}),
          ...(refSenderOrganisationName ? { organisationName: refSenderOrganisationName } : {}),
          ...(refSenderMedicalSpecialityCode ? { medicalSpecialityCode: refSenderMedicalSpecialityCode } : {}),
          referrer: {
            ...(refReferrerPersonName ? { personName: refReferrerPersonName } : {}),
            ...(refReferrerPersonTitle ? { personTitle: refReferrerPersonTitle } : {}),
          },
        },
        receiver: {
          ean: refReceiverEan,
          ...(refReceiverIdentifier ? { identifier: refReceiverIdentifier } : {}),
          ...(refReceiverIdentifierCode ? { identifierCode: refReceiverIdentifierCode } : {}),
        },
        patient: {
          ...(refPatientCpr ? { civilRegistrationNumber: refPatientCpr } : {}),
          ...(refPatientSurname ? { personSurnameName: refPatientSurname } : {}),
          ...(refPatientGivenName ? { personGivenName: refPatientGivenName } : {}),
        },
        ...(hasEpisodeOfCare
          ? { episodesOfCare: [{ type: refEpisodeOfCareType || 'henviser', identifier: refEpisodeOfCareId }] }
          : {}),
        ...(hasEpisodeOfCareIdentifier ? { episodeOfCareIdentifier: refEpisodeOfCareIdentifier } : {}),
        ...(anamnesisLines.length > 0
          ? { relevantClinicalInformation: { anamnesis: anamnesisLines } }
          : {}),
        ...(refKind !== 'XREF06'
          ? {
              additionalInformation: {
                clinicalReason: toLines(refClinicalReason).map((line) => line.trim()).filter((line) => line.length > 0),
              },
            }
          : {}),
        ...(hasReferral
          ? {
              referral: {
                refer: {
                  ...(refKind !== 'XREF06' && refDiagnoseDescriptionCode
                    ? { diagnoseDescriptionCode: refDiagnoseDescriptionCode }
                    : {}),
                  ...(refDiagnoseCode ? { diagnoseCode: refDiagnoseCode } : {}),
                  ...(refDiagnoseTypeCode ? { diagnoseTypeCode: refDiagnoseTypeCode } : {}),
                  ...(refDiagnoseText ? { diagnoseText: refDiagnoseText } : {}),
                },
              },
            }
          : {}),
      });
      setRefPreview(result);
    } catch (err) {
      setRefError(referralBuildFailureMessage(err, 'Kunne ikke bygge henvisningen.'));
    } finally {
      setRefBusy(false);
    }
  };

  // ── Log helpers ───────────────────────────────────────────────────────────

  /** Build a map from envelope_identifier → id for out-rows. Used to highlight coupling. */
  const outByEnvelope = new Map<string, string>();
  for (const row of exchanges) {
    if (row.direction === 'out' && row.envelopeIdentifier) {
      outByEnvelope.set(row.envelopeIdentifier, row.id);
    }
  }

  /** Two in-rows with the same envelope+letter are doublets. */
  const doubletKeys = new Set<string>();
  const seen = new Map<string, number>();
  for (const row of exchanges) {
    if (row.direction === 'in' && row.envelopeIdentifier && row.letterIdentifier) {
      const key = `${row.envelopeIdentifier}|${row.letterIdentifier}`;
      seen.set(key, (seen.get(key) ?? 0) + 1);
    }
  }
  for (const [key, count] of seen) {
    if (count > 1) doubletKeys.add(key);
  }

  // CTL-Receive 2.4: derive the effective acknowledgement state per original envelope.
  // Negative (XCTL02) takes precedence over positive (XCTL03) regardless of import order.
  // A positive followed by a negative → negativ. A negative followed by a positive → negativ.
  const effectiveAckByOriginalEnvelope = new Map<string, 'positiv' | 'negativ'>();
  for (const row of exchanges) {
    if (row.direction === 'in' && row.originalEnvelopeIdentifier) {
      const current = effectiveAckByOriginalEnvelope.get(row.originalEnvelopeIdentifier);
      if (row.kind === 'XCTL02') {
        // Negative always wins, overwriting any prior positive.
        effectiveAckByOriginalEnvelope.set(row.originalEnvelopeIdentifier, 'negativ');
      } else if (row.kind === 'XCTL03' && current !== 'negativ') {
        // Positive only if a negative has not already been seen for this original envelope.
        effectiveAckByOriginalEnvelope.set(row.originalEnvelopeIdentifier, 'positiv');
      }
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="flex w-full min-w-0 flex-wrap items-start justify-between gap-s3 nav:flex-nowrap nav:items-center min-h-10">
        <div className="shrink-0">
          <h1 className={cn(TYPE.title, 'text-ds-text')}>{t('medcomSection.title')}</h1>
        </div>
        <div className="w-[200px] max-w-full shrink-0">
          <Select
            aria-label={t('medcomSection.standard_label')}
            value={standard}
            onChange={(event) => {
              const next = event.target.value as StandardId;
              setStandard(next);
              setPreview(null);
              setBuildError(null);
              setImportResult(null);
              setImportError(null);
              setRefPreview(null);
              setRefError(null);
              if (next === 'REF01') applyRefDefaults('XREF01');
              else if (next === 'REF02') applyRefDefaults('XREF02');
              else if (next === 'REF06') applyRefDefaults('XREF06');
            }}
            className="w-full"
          >
            {STANDARDS.map((item) => (
              <option key={item.id} value={item.id} disabled={!item.ready}>
                {item.label}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <div className="mt-s5">
      <Card>
        <div className="grid gap-s4">

          {IMPORT_ONLY_STANDARD_IDS.has(standard) ? (
            <p className="text-sm text-ds-muted">
              {EPIKRISE_STANDARD_KIND[standard]
                ? `${standard}: indlæs en modtaget ${EPIKRISE_STANDARD_KIND[standard]} i importpanelet nedenfor; den vises under 'Modtagne epikriser'. Positiv kvittering (XCTL03) dannes kun ved pluspositivkvitt. Epikriser sendes med 'Dan epikrise' på en modtaget henvisning.`
                : standard === 'DIS13'
                ? "DIS13: indlæs et modtaget bookingsvar (XDIS13) i importpanelet nedenfor; det vises under 'Modtagne bookingsvar' og på patientens Korrespondance-fane. Positiv kvittering (XCTL03) dannes kun ved pluspositivkvitt."
                : standard === 'GGOP'
                ? "GGOP: indlæs en modtaget genoptræningsplan (GGOP1/2/3) i importpanelet nedenfor; den vises under 'Modtagne genoptræningsplaner' og på patientens Medcom-fane. Positiv kvittering (XCTL03) dannes kun ved pluspositivkvitt. Genoptræningsplaner dannes fra patientsiden (Genoptræningsplan)."
                : standard === 'XDIS'
                ? "XDIS (hjemmepleje): indlæs en modtaget indlæggelsesrapport (XDIS16) eller korrespondance (XDIS91) i importpanelet nedenfor. XDIS16 vises under 'Modtagne indlæggelsesrapporter' og på patientens Medcom-fane, hvor plejeforløbsplanen (XDIS21) og udskrivningsrapporten (XDIS18) dannes. En XDIS91 med forløbsnummer besvares med 'Besvar'; ændringer kan indføres i plejeforløbsplanen."
                : standard === 'RPT01'
                ? "RPT01 (R0131K, EDI) og XRPT01 (XR0131K, XML 2014/10/08): indlæs et laboratoriesvar i importpanelet nedenfor. RPT01 danner CTL02/CTL03 i EDI (uden VANSEnvelope). XRPT01 danner XCTL02/XCTL03 i XML. Forkert version giver problem_med_version; ukendt type giver problem_med_modtagerID."
                : standard === 'XRPT05'
                ? "XRPT05: indlæs et mikrobiologisvar (MicrobiologyWebReport, XR0532M, XML 2014/10/08) i importpanelet nedenfor. Positiv/negativ XCTL02/XCTL03 dannes som for XRPT01."
                : standard === 'VANS'
                ? 'VANSEnvelope: indlæs en modtaget kuvert i importpanelet nedenfor. Afsendelse bygges ikke på denne side.'
                : 'BIN01: indlæs en modtaget XBIN01/XBIN02 i importpanelet nedenfor. Bilag sendes fra henvisningsformularerne, og korrespondance (XDIS91 + XBIN01) fra patientsiden.'}
            </p>
          ) : null}
          {standard === 'XDIS' && activeClinicId ? (
            // Spor A block 3 (XDIS16/18/91): received admission reports and XDIS91 to answer.
            <>
              <ReceivedXdis16Panel clinicId={activeClinicId} refreshKey={correspondenceRefresh} />
              <ReceivedCorrespondencePanel clinicId={activeClinicId} refreshKey={correspondenceRefresh} />
            </>
          ) : null}
          {standard === 'BIN01' && activeClinicId ? (
            <ReceivedCorrespondencePanel clinicId={activeClinicId} refreshKey={correspondenceRefresh} />
          ) : null}
          {EPIKRISE_STANDARD_KIND[standard] && activeClinicId ? (
            <ReceivedEpikriserPanel
              key={standard}
              clinicId={activeClinicId}
              kind={EPIKRISE_STANDARD_KIND[standard]}
              refreshKey={correspondenceRefresh}
              canAttach={canImport}
            />
          ) : null}
          {standard === 'DIS13' && activeClinicId ? (
            <ReceivedBookingsvarPanel clinicId={activeClinicId} refreshKey={correspondenceRefresh} canAttach={canImport} />
          ) : null}
          {standard === 'GGOP' && activeClinicId ? (
            <ReceivedGgopPanel clinicId={activeClinicId} refreshKey={correspondenceRefresh} />
          ) : null}
          {standard === 'RPT01' && activeClinicId ? (
            <>
              <ReceivedLabReportsPanel
                clinicId={activeClinicId}
                kind="XRPT01"
                refreshKey={correspondenceRefresh}
                canAttach={canImport}
              />
              <LabPartiesPanel clinicId={activeClinicId} refreshKey={correspondenceRefresh} />
              <LabAnalysisCataloguePanel clinicId={activeClinicId} refreshKey={correspondenceRefresh} />
            </>
          ) : null}
          {standard === 'XRPT05' && activeClinicId ? (
            <>
              <ReceivedLabReportsPanel
                clinicId={activeClinicId}
                kind="XRPT05"
                refreshKey={correspondenceRefresh}
                canAttach={canImport}
              />
              <LabPartiesPanel clinicId={activeClinicId} refreshKey={correspondenceRefresh} />
              <LabAnalysisCataloguePanel clinicId={activeClinicId} refreshKey={correspondenceRefresh} />
            </>
          ) : null}
          {standard === 'SUP' && activeClinicId ? (
            // T2.6 (SUP-S 4.2): 'Dan SUP-udtræk' and the export log. Admin only.
            <SupExportPanel clinicId={activeClinicId} canExport={canExportSup} />
          ) : null}
          {IMPORT_ONLY_STANDARD_IDS.has(standard) || standard === 'SUP' ? null : standard !== 'CTL' && !REF_STANDARD_IDS.has(standard) ? (
            <p className="text-sm text-ds-muted">{standard} er ikke bygget endnu.</p>
          ) : standard === 'CTL' ? (
            <>
              {/* ── Build form ────────────────────────────────────────────── */}
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Kvitteringstype</span>
                <select
                  className="w-full rounded-md border border-ds-field px-3 py-2 text-sm"
                  value={kind}
                  onChange={(event) => {
                    setKind(event.target.value as Kind);
                    setPreview(null);
                    setBuildError(null);
                  }}
                >
                  {KINDS.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Afsender EAN</span>
                <Input value={senderEan} onChange={(event) => setSenderEan(event.target.value)} />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Modtager EAN</span>
                <Input value={receiverEan} onChange={(event) => setReceiverEan(event.target.value)} />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Original kuvert-nummer</span>
                <Input
                  value={originalEnvelopeIdentifier}
                  maxLength={14}
                  onChange={(event) => setOriginalEnvelopeIdentifier(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Original afsender EAN</span>
                <Input
                  value={originalSenderEan}
                  onChange={(event) => setOriginalSenderEan(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Original modtager EAN</span>
                <Input
                  value={originalReceiverEan}
                  onChange={(event) => setOriginalReceiverEan(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Kuvert-nummer</span>
                <Input
                  value={envelopeIdentifier}
                  maxLength={14}
                  hint="Dannes automatisk, hvis feltet er tomt."
                  onChange={(event) => setEnvelopeIdentifier(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Brev-nummer</span>
                <Input
                  value={letterIdentifier}
                  maxLength={14}
                  hint="Dannes automatisk, hvis feltet er tomt."
                  onChange={(event) => setLetterIdentifier(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Dato (YYYY-MM-DD)</span>
                <Input
                  value={sentDate}
                  hint="Nu, hvis feltet er tomt."
                  onChange={(event) => setSentDate(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Klokkeslæt (HH:MM)</span>
                <Input
                  value={sentTime}
                  hint="Nu, hvis feltet er tomt."
                  onChange={(event) => setSentTime(event.target.value)}
                />
              </label>
              {negative && (
                <>
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium">Afvisningskode</span>
                    <select
                      className="w-full rounded-md border border-ds-field px-3 py-2 text-sm"
                      value={refuseCode}
                      onChange={(event) => setRefuseCode(event.target.value as RefuseCode)}
                    >
                      <option value="">(ingen)</option>
                      {REFUSE_CODES.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium">Afvisningstekst</span>
                    <Textarea
                      rows={3}
                      value={refuseText}
                      hint="Én linje bliver til én tekstlinje i kvitteringen."
                      onChange={(event) => setRefuseText(event.target.value)}
                    />
                  </label>
                </>
              )}

              <div>
                <span className="mb-1 block text-sm font-medium">Originale breve</span>
                {originalLetters.length > 0 && (
                  <ul className="space-y-2 text-sm">
                    {originalLetters.map((letter, index) => (
                      <li key={index} className="flex items-center justify-between gap-3">
                        <Input
                          value={letter.identifier}
                          maxLength={14}
                          placeholder="Brev-nummer"
                          onChange={(event) =>
                            setOriginalLetters((current) =>
                              current.map((entry, entryIndex) =>
                                entryIndex === index
                                  ? { ...entry, identifier: event.target.value }
                                  : entry,
                              ),
                            )
                          }
                        />
                        <Input
                          value={letter.versionCode}
                          maxLength={7}
                          placeholder="Version"
                          onChange={(event) =>
                            setOriginalLetters((current) =>
                              current.map((entry, entryIndex) =>
                                entryIndex === index
                                  ? { ...entry, versionCode: event.target.value }
                                  : entry,
                              ),
                            )
                          }
                        />
                        <button
                          type="button"
                          className="text-ds-muted"
                          onClick={() =>
                            setOriginalLetters((current) =>
                              current.filter((_, entryIndex) => entryIndex !== index),
                            )
                          }
                        >
                          Fjern
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  type="button"
                  className="mt-2 text-sm text-ds-muted"
                  disabled={originalLetters.length >= MAX_ORIGINAL_LETTERS}
                  onClick={() =>
                    setOriginalLetters((current) =>
                      current.length >= MAX_ORIGINAL_LETTERS
                        ? current
                        : [...current, { identifier: '', versionCode: '' }],
                    )
                  }
                >
                  Tilføj brev
                </button>
              </div>

              <div>
                <Button type="button" onClick={() => void onCheck()} disabled={buildBusy}>
                  <Receipt className="mr-2 h-4 w-4" />
                  {buildBusy ? 'Bygger…' : 'Byg kvittering'}
                </Button>
              </div>
            </>
          ) : null}
        </div>

        {buildError && (
          <div className="mt-4">
            <ErrorAlert message={buildError} />
          </div>
        )}

        {preview && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold">
              {preview.ok ? 'Kvitteringen består tjekket' : 'Kvitteringen fejler tjekket'}
            </h2>
            <p className="mt-1 text-sm text-ds-muted">Kunde: {preview.consumerName}</p>
            <div className="mt-4 flex gap-2">
              <Button type="button" onClick={() => downloadMedcomDocument(`${preview.kind}.xml`, preview.xml)}>
                Hent kvittering
              </Button>
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              {preview.checks.map((item) => (
                <li key={item.id}>
                  <span className={item.pass ? 'text-ds-green-ink' : 'text-ds-red-ink'}>
                    {item.pass ? 'Ok' : 'Fejl'}
                  </span>
                  {' — '}
                  {item.detail}
                </li>
              ))}
            </ul>
            <pre className="mt-4 max-h-80 overflow-auto rounded-md bg-ds-toast-bg p-4 text-xs text-ds-toast-ink">
              {preview.xml}
            </pre>
          </div>
        )}

        {/* ── Import panel — CTL-Receive 1.3 + CTL-Send 2.1–2.4; REF06 receive (read referral from file) */}
        {canImport && IMPORT_STANDARD_IDS.has(standard) && (
          <div className="mt-8 border-t border-ds-line pt-6">
            <h2 className="text-lg font-semibold">Importer meddelelse eller kvittering (kun admin)</h2>
            <p className="mt-1 text-sm text-ds-muted">
              Indlæs en MedCom-meddelelse (fx XREF01, XREF02, XREF06, XBIN01, XDIS01/02/03/05, XDIS13, XDIS16, XDIS91, RPT01 (EDI), XRPT01, XRPT05) eller en modtaget
              XCONTRL-kvittering. Systemet afgør automatisk hvad det er og returnerer den
              relevante kvittering. Meddelelser gemmes, også hvis de afvises.
            </p>
            <div className="mt-4">
              <label htmlFor="medcom-standarder-import-file" className="mb-1 block text-sm font-medium">
                {t('medcomSection.load_xml_from_file')}
              </label>
              <input
                id="medcom-standarder-import-file"
                ref={importFileInputRef}
                type="file"
                accept=".xml,.edi,text/xml"
                className="sr-only"
                onChange={(event) => {
                  const picked = event.target.files?.[0];
                  event.target.value = '';
                  if (!picked) return;
                  void picked.arrayBuffer().then((bytes) => {
                    setImportXml(decodeMedcomImportFile(bytes, picked.name));
                    setImportFileName(picked.name);
                    setImportResult(null);
                    setImportError(null);
                  });
                }}
              />
              <div className="mt-1 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => importFileInputRef.current?.click()}
                >
                  {t('medcomSection.choose_xml_file')}
                </Button>
                <LoadedImportFileName filename={importFileName} />
              </div>
            </div>
            <label className="mt-4 block text-sm">
              <span className="mb-1 block font-medium">XML / EDI</span>
              <textarea
                className="mt-1 w-full rounded-md border border-ds-field px-3 py-2 text-xs font-mono"
                rows={6}
                value={importXml}
                placeholder="<?xml … ?> eller UNA:+.? ' / UNB+UNOC:3+…"
                onChange={(event) => {
                  setImportXml(event.target.value);
                  setImportFileName(null);
                  setImportResult(null);
                  setImportError(null);
                }}
              />
            </label>
            <div className="mt-3">
              <Button type="button" onClick={() => void onImport()} disabled={importBusy}>
                {importBusy ? 'Importerer…' : 'Importer'}
              </Button>
            </div>

            {importError && (
              <div className="mt-4">
                <ErrorAlert message={importError} />
              </div>
            )}

            {importResult && 'importKind' in importResult && importResult.importKind === 'message' ? (
              /* ── Message import result (CTL-Send 2.1–2.4) ─────────────────── */
              <div className="mt-4 rounded-md border border-ds-line bg-ds-sunk p-4 text-sm">
                <LoadedImportFileName filename={importFileName} className="mb-2" />
                <p className="font-medium">
                  {importResult.refuseCode !== null ? (
                    <span className="text-ds-red-ink">
                      Meddelelse afvist — {importResult.typeCode}
                    </span>
                  ) : importResult.generatedReceiptKind === 'XCTL03' ||
                    importResult.generatedReceiptKind === 'CTL03' ? (
                    <span className="text-ds-green-ink">
                      Meddelelse accepteret — positiv kvittering dannet ({importResult.typeCode})
                    </span>
                  ) : (
                    <span className="text-ds-text">
                      Meddelelse accepteret — ingen kvittering krævet ({importResult.typeCode})
                    </span>
                  )}
                </p>
                <div className="mt-2">
                  <CtlReceiptExplanation
                    values={{
                      typeCode: importResult.typeCode,
                      versionCode: importResult.versionCode,
                      acknowledgementCode: importResult.acknowledgementCode,
                      envelopeIdentifier: importResult.envelopeIdentifier,
                      letterIdentifier: importResult.letterIdentifier,
                      senderEan: importResult.senderEan,
                      receiverEan: importResult.receiverEan,
                      refuseCode: importResult.refuseCode,
                      refuseReason: importResult.refuseReason,
                      generatedReceiptKind: importResult.generatedReceiptKind,
                    }}
                  />
                  <dl className="mt-1 space-y-1 text-xs text-ds-text">
                    <div className="flex gap-2">
                      <dt className="w-48 shrink-0 font-medium">VANSEnvelope</dt>
                      <dd>
                        {importResult.inboundVansWrapped
                          ? 'Ja — indgående var indpakket'
                          : 'Nej — indgående var ikke indpakket'}
                      </dd>
                    </div>
                  </dl>
                </div>
                {/* CTL-Send 3.16–3.17: log entry for the generated receipt */}
                {importResult.generatedReceiptXml && (
                  <div className="mt-3">
                    <Button
                      type="button"
                      onClick={() =>
                        downloadMedcomDocument(
                          ctlReceiptDownloadFilename(
                            importResult.generatedReceiptKind ?? 'XCTL',
                            importResult.envelopeIdentifier,
                            importResult.generatedReceiptId ?? importResult.id,
                          ),
                          importResult.generatedReceiptXml!,
                        )
                      }
                    >
                      Hent kvittering ({importResult.generatedReceiptKind})
                    </Button>
                  </div>
                )}
                {/* CTL-Send 2.6 / 4.3: VANS-wrapped receipt when inbound was VANSEnvelope */}
                {importResult.generatedReceiptVansXml && (
                  <div className="mt-3">
                    <Button
                      type="button"
                      onClick={() =>
                        downloadMedcomDocument(
                          `${importResult.generatedReceiptKind ?? 'XCTL'}-VANS.xml`,
                          importResult.generatedReceiptVansXml!,
                        )
                      }
                    >
                      Hent kvittering i VANSEnvelope
                    </Button>
                  </div>
                )}
                <VansReceiptBlock receipt={importResult.vansReceipt} note={importResult.vansReceiptNote} />
                {importResult.generatedReceiptKind && !importResult.generatedReceiptXml && (
                  <p className="mt-3 text-sm text-ds-amber-ink">
                    Kvittering dannet men XML ikke tilgængeligt (EAN-fejl i meddelelsen).
                  </p>
                )}
                {/* CTL-Send 2.3/2.4: no receipt when only negative was requested */}
                {importResult.refuseCode === null &&
                  importResult.generatedReceiptKind === null &&
                  !importResult.receiptForMessageId && (
                  <p className="mt-3 text-sm text-ds-muted">
                    Ingen kvittering dannet (meddelelsen beder kun om negativ kvittering)
                  </p>
                )}
                {/* Doublet — CTL-Send 3.22 */}
                {importResult.doublet && (
                  <p className="mt-3 font-medium text-ds-amber-ink">
                    Dobbeltindlæsning: denne meddelelse er indlæst før (samme kuvert- og brev-nummer).
                  </p>
                )}
                {/* Prior receipt for this message (reverse coupling) — only a stored earlier receipt */}
                {importResult.receiptForMessageId && !importResult.generatedReceiptId && (
                  <p className="mt-2 text-ds-green-ink">
                    En kvittering for denne meddelelse er allerede dannet (id {importResult.receiptForMessageId}).
                  </p>
                )}
              </div>
            ) : importResult && 'importKind' in importResult && importResult.importKind === 'vans-receipt' ? (
              /* ── VANS receipt import (C17) ──────────────────────────────────── */
              <div className="mt-4 rounded-md border border-ds-line bg-ds-sunk p-4 text-sm">
                <LoadedImportFileName filename={importFileName} className="mb-2" />
                <p className="font-medium">
                  {importResult.receiptKind === 'PositiveMessage' ? (
                    <span className="text-ds-green-ink">Positiv VANS-kvittering importeret</span>
                  ) : (
                    <span className="text-ds-red-ink">Negativ VANS-kvittering importeret ({importResult.receiptKind})</span>
                  )}
                </p>
                <dl className="mt-2 space-y-1 text-xs text-ds-text">
                  <div className="flex gap-2">
                    <dt className="w-48 shrink-0 font-medium">EnvelopeIdentifier</dt>
                    <dd className="font-mono">{importResult.envelopeIdentifier || '—'}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-48 shrink-0 font-medium">OriginalEnvelopeIdentifier</dt>
                    <dd className="font-mono">{importResult.originalEnvelopeIdentifier ?? '—'}</dd>
                  </div>
                  {(importResult.errorCode || importResult.errorText) && (
                    <div className="flex gap-2">
                      <dt className="w-48 shrink-0 font-medium text-ds-red-ink">Fejl</dt>
                      <dd className="whitespace-pre-line text-ds-red-ink">
                        {[importResult.errorCode, importResult.errorText].filter(Boolean).join(': ')}
                      </dd>
                    </div>
                  )}
                </dl>
                {importResult.checks.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs">
                    {importResult.checks.map((check) => (
                      <li key={check.id} className={check.pass ? 'text-ds-green-ink' : 'text-ds-red-ink'}>
                        {check.pass ? '✓' : '✗'} {check.detail}
                      </li>
                    ))}
                  </ul>
                )}
                {importResult.coupledToId ? (
                  <p className={importResult.matchesSent ? 'mt-2 text-ds-green-ink' : 'mt-2 text-ds-red-ink'}>
                    Knyttet til afsendt VANSEnvelope med id {importResult.coupledToId}
                    {importResult.matchesSent ? '; OriginalMessage svarer til det sendte.' : '; OriginalMessage afviger fra det sendte.'}
                  </p>
                ) : (
                  <p className="mt-2 text-ds-muted">
                    Ingen afsendt VANSEnvelope med dette OriginalEnvelopeIdentifier. Kvitteringen er gemt.
                  </p>
                )}
              </div>
            ) : importResult && 'importKind' in importResult && importResult.importKind === 'vans-rejected' ? (
              /* ── VANSEnvelope refused (C21) ─────────────────────────────────── */
              <div className="mt-4 rounded-md border border-ds-red-line bg-ds-red-tint p-4 text-sm">
                <LoadedImportFileName filename={importFileName} className="mb-2" />
                <p className="font-medium text-ds-red-ink">VANSEnvelope afvist</p>
                <p className="mt-1 whitespace-pre-line text-xs text-ds-red-ink">{importResult.errorText}</p>
                <VansReceiptBlock receipt={importResult.vansReceipt} />
              </div>
            ) : importResult && !('importKind' in importResult) ? (
              /* ── Receipt import result (CTL-Receive 1.3) ──────────────────── */
              <div className="mt-4 rounded-md border border-ds-line bg-ds-sunk p-4 text-sm">
                <LoadedImportFileName filename={importFileName} className="mb-2" />
                <p className="font-medium">
                  {(importResult as ReceiptImportResult).validationOk ? (
                    <span className="text-ds-green-ink">Gyldig kvittering importeret</span>
                  ) : (
                    <span className="text-ds-red-ink">Ugyldig kvittering importeret</span>
                  )}
                </p>
                <dl className="mt-2 space-y-1 text-xs text-ds-text">
                  <div className="flex gap-2">
                    <dt className="w-40 shrink-0 font-medium">Type</dt>
                    <dd>{(importResult as ReceiptImportResult).kind}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-40 shrink-0 font-medium">Kuvert-nummer</dt>
                    <dd>{(importResult as ReceiptImportResult).envelopeIdentifier}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-40 shrink-0 font-medium">Brev-nummer</dt>
                    <dd>{(importResult as ReceiptImportResult).letterIdentifier}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-40 shrink-0 font-medium">Afsender</dt>
                    <dd>{(importResult as ReceiptImportResult).senderEan}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-40 shrink-0 font-medium">Modtager</dt>
                    <dd>{(importResult as ReceiptImportResult).receiverEan}</dd>
                  </div>
                  {(importResult as ReceiptImportResult).originalEnvelopeIdentifier && (
                    <div className="flex gap-2">
                      <dt className="w-40 shrink-0 font-medium">Original kuvert</dt>
                      <dd>{(importResult as ReceiptImportResult).originalEnvelopeIdentifier}</dd>
                    </div>
                  )}
                  {(importResult as ReceiptImportResult).refuseCode && (
                    <div className="flex gap-2">
                      <dt className="w-40 shrink-0 font-medium">Afvisningskode</dt>
                      <dd>{(importResult as ReceiptImportResult).refuseCode}</dd>
                    </div>
                  )}
                  {(importResult as ReceiptImportResult).refuseText && (
                    <div className="flex gap-2">
                      <dt className="w-40 shrink-0 font-medium">Afvisningstekst</dt>
                      <dd className="whitespace-pre-line">{(importResult as ReceiptImportResult).refuseText}</dd>
                    </div>
                  )}
                  {(importResult as ReceiptImportResult).missingMandatory.length > 0 && (
                    <div className="flex gap-2">
                      <dt className="w-40 shrink-0 font-medium text-ds-red-ink">Mangler</dt>
                      <dd className="text-ds-red-ink">
                        {(importResult as ReceiptImportResult).missingMandatory.join(', ')}
                      </dd>
                    </div>
                  )}
                </dl>
                {/* CTL-Send 3.24: doublet flag */}
                {(importResult as ReceiptImportResult).doublet && (
                  <p className="mt-3 font-medium text-ds-amber-ink">
                    Dobbeltindlæsning: denne kvittering er indlæst før (samme kuvert- og brev-nummer).
                  </p>
                )}
                {/* CTL-Receive 3.4, 3.8: coupling */}
                {(importResult as ReceiptImportResult).coupledToId ? (
                  <p className="mt-2 text-ds-green-ink">
                    Kvitteringen er knyttet til afsendt meddelelse med id{' '}
                    {(importResult as ReceiptImportResult).coupledToId}.
                  </p>
                ) : (
                  <p className="mt-2 text-ds-muted">
                    Ingen afsendt meddelelse med dette original kuvert-nummer. Kvitteringen er gemt.
                  </p>
                )}
                <VansReceiptBlock
                  receipt={(importResult as ReceiptImportResult).vansReceipt}
                  note={(importResult as ReceiptImportResult).vansReceiptNote}
                />
              </div>
            ) : null}
          </div>
        )}

        {/* ── Exchange log — CTL-Send 2.5; REF06 receipt visibility */}
        {IMPORT_STANDARD_IDS.has(standard) && (
          <div className="mt-s5 border-t border-ds-line pt-s5">
            <div className="flex flex-wrap items-center justify-between gap-s3">
              <h2 className={cn(TYPE.heading, 'text-ds-text')}>{t('medcomSection.exchange_log')}</h2>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={logBusy}
                onClick={() => void loadLog()}
              >
                {logBusy ? t('medcom.correspondence.refreshing') : t('medcom.correspondence.refresh')}
              </Button>
            </div>
            <p className={cn(TYPE.body, 'mt-s1 text-ds-muted')}>
              Afsendte og modtagne meddelelser og kvitteringer, nyeste øverst.
            </p>
            <form
              className="mt-s3 flex flex-wrap items-end gap-s3"
              data-testid="exchange-log-filters"
              onSubmit={(event) => {
                event.preventDefault();
                logFirstIdRef.current = null;
                setLogSearch(logDraftSearch);
                setLogKind(logDraftKind);
                setLogOffset(0);
              }}
            >
              <Input
                label={t('medcomSection.exchange_search')}
                name="exchange-search"
                value={logDraftSearch}
                onChange={(event) => setLogDraftSearch(event.target.value)}
                placeholder={t('medcomSection.exchange_search_placeholder')}
              />
              <div>
                <label className="block text-sm font-medium text-ds-text" htmlFor="exchange-kind">
                  {t('medcomSection.exchange_type_filter')}
                </label>
                <input
                  id="exchange-kind"
                  list="exchange-kind-options"
                  value={logDraftKind}
                  onChange={(event) => setLogDraftKind(event.target.value)}
                  className="mt-1 block h-[44px] w-full rounded-md border border-ds-field bg-ds-card px-3 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:ring-ds-teal sm:w-44"
                  placeholder={t('medcomSection.exchange_type_all')}
                />
                <datalist id="exchange-kind-options">
                  {EXCHANGE_KIND_OPTIONS.filter(Boolean).map((kind) => (
                    <option key={kind} value={kind} />
                  ))}
                </datalist>
              </div>
              <Button type="submit" variant="secondary" size="sm">
                {t('medcomSection.exchange_search_submit')}
              </Button>
            </form>

            {logError ? (
              <div className="mt-s4">
                <Banner
                  tone="error"
                  action={
                    <Button type="button" variant="secondary" size="sm" onClick={() => void loadLog()}>
                      {t('app.retry')}
                    </Button>
                  }
                >
                  {logError}
                </Banner>
              </div>
            ) : null}

            {logBusy && exchanges.length === 0 ? (
              <Card className="mt-s4">
                <div className="flex flex-col gap-s3" aria-busy>
                  {Array.from({ length: 6 }, (_, index) => (
                    <Skeleton key={index} className="h-10 w-full" />
                  ))}
                </div>
              </Card>
            ) : exchanges.length === 0 && !logError ? (
              <Card className="mt-s4">
                <EmptyState icon={<Inbox className="h-7 w-7" />} title={t('medcomSection.empty_exchange_log')} />
              </Card>
            ) : (
              <Card className="mt-s4 p-0 nav:p-0">
                <ResponsiveTable
                  columns={[
                    {
                      id: 'direction',
                      header: t('communicate.col_direction'),
                      cell: (row) => (
                        <span
                          className={
                            row.direction === 'out'
                              ? 'font-medium text-ds-teal'
                              : 'font-medium text-ds-text'
                          }
                        >
                          {row.direction === 'out'
                            ? t('medcomSection.exchange_direction_out')
                            : t('medcomSection.exchange_direction_in')}
                        </span>
                      ),
                    },
                    {
                      id: 'type',
                      header: t('communicate.col_type'),
                      cell: (row) => row.kind,
                    },
                    {
                      id: 'envelope',
                      header: t('medcomSection.exchange_col_envelope'),
                      cell: (row) => (
                        <span>
                          <span className="font-mono">{row.envelopeIdentifier ?? '—'}</span>
                          {row.originalEnvelopeIdentifier ? (
                            <span className="mt-s1 block text-ds-muted">
                              Orig.{' '}
                              <span className="font-mono">{row.originalEnvelopeIdentifier}</span>
                            </span>
                          ) : null}
                        </span>
                      ),
                      hideOnMobile: true,
                    },
                    {
                      id: 'letter',
                      header: t('medcomSection.exchange_col_letter'),
                      cell: (row) => (
                        <span>
                          <span className="font-mono">{row.letterIdentifier ?? '—'}</span>
                          {row.originalLetterIdentifier ? (
                            <span className="mt-s1 block text-ds-muted">
                              Orig.{' '}
                              <span className="font-mono">{row.originalLetterIdentifier}</span>
                            </span>
                          ) : null}
                        </span>
                      ),
                      hideOnMobile: true,
                    },
                    {
                      id: 'sender',
                      header: t('communicate.col_sender'),
                      cell: (row) => row.senderEan ?? '—',
                      hideOnMobile: true,
                    },
                    {
                      id: 'receiver',
                      header: t('communicate.col_receiver'),
                      cell: (row) => row.receiverEan ?? '—',
                      hideOnMobile: true,
                    },
                    {
                      id: 'valid',
                      header: t('medcomSection.exchange_col_valid'),
                      cell: (row) => (
                        <span className={row.validationOk ? 'text-ds-green-ink' : 'text-ds-red-ink'}>
                          {row.validationOk ? 'Ja' : 'Nej'}
                        </span>
                      ),
                    },
                    {
                      id: 'refuse',
                      header: t('medcomSection.exchange_col_refuse'),
                      cell: (row) => row.refuseCode ?? '—',
                      hideOnMobile: true,
                    },
                    {
                      id: 'time',
                      header: t('medcomSection.exchange_col_time'),
                      cell: (row) =>
                        row.importedAt
                          ? `Importeret ${formatTs(row.importedAt)}`
                          : formatTs(row.createdAt),
                    },
                    {
                      id: 'notes',
                      header: t('medcomSection.exchange_col_notes'),
                      cell: (row) => (
                        <ExchangeLogNotes
                          row={row}
                          doubletKeys={doubletKeys}
                          outByEnvelope={outByEnvelope}
                          effectiveAckByOriginalEnvelope={effectiveAckByOriginalEnvelope}
                        />
                      ),
                      hideOnMobile: true,
                    },
                    {
                      id: 'actions',
                      header: t('medcomSection.exchange_col_actions'),
                      cell: (row) =>
                        isSentCtlReceipt(row) ? (
                          <span className="flex flex-wrap gap-s2">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => void openReceiptDetail(row)}
                            >
                              {t('medcomSection.exchange_open_details')}
                            </Button>
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => void downloadReceipt(row)}
                            >
                              {t('medcomSection.download_receipt')}
                            </Button>
                          </span>
                        ) : null,
                    },
                  ]}
                  rows={exchanges}
                  getRowKey={(row) => row.id}
                  empty={null}
                />
                <div className="flex flex-wrap items-center gap-s3 border-t border-ds-line px-s4 py-s3">
                  {logOffset > 0 ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={logBusy}
                      onClick={() => setLogOffset(Math.max(0, logOffset - CTL_PREVIEW_DEFAULT_LIMIT))}
                    >
                      {t('medcomSection.exchange_newer')}
                    </Button>
                  ) : null}
                  {logHasMore ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      data-testid="exchange-log-older"
                      disabled={logBusy}
                      onClick={() => setLogOffset(logOffset + CTL_PREVIEW_DEFAULT_LIMIT)}
                    >
                      {t('medcomSection.exchange_older')}
                    </Button>
                  ) : null}
                </div>
              </Card>
            )}
            {receiptError && !receiptDetail ? (
              <div className="mt-s4">
                <ErrorAlert message={receiptError} />
              </div>
            ) : null}
            {receiptDetail ? (
              <Modal
                title={t('medcomSection.exchange_receipt_details_title', {
                  kind: receiptDetail.kind,
                })}
                open
                onClose={() => {
                  setReceiptDetail(null);
                  setReceiptXml(null);
                  setReceiptError(null);
                }}
                footer={
                  <Button
                    type="button"
                    onClick={() => void downloadReceipt(receiptDetail, receiptXml)}
                    disabled={receiptBusy || !receiptXml}
                  >
                    {t('medcomSection.download_receipt')}
                  </Button>
                }
              >
                <CtlReceiptExplanation
                  values={{
                    kind: receiptDetail.kind,
                    envelopeIdentifier: receiptDetail.envelopeIdentifier,
                    letterIdentifier: receiptDetail.letterIdentifier,
                    senderEan: receiptDetail.senderEan,
                    receiverEan: receiptDetail.receiverEan,
                    originalEnvelopeIdentifier: receiptDetail.originalEnvelopeIdentifier,
                    originalLetterIdentifier: receiptDetail.originalLetterIdentifier,
                    refuseCode:
                      receiptDetail.refuseCode ??
                      (receiptXml ? ctlRefuseCodeFromXml(receiptXml) : null),
                    refuseReason:
                      receiptDetail.refuseReason ??
                      receiptDetail.refuseText ??
                      (receiptXml ? ctlRefuseTextsFromXml(receiptXml).join('\n') : null),
                    createdAt: receiptDetail.createdAt,
                    importedAt: receiptDetail.importedAt,
                  }}
                  formatTs={formatTs}
                />
                {receiptBusy ? (
                  <p className={cn(TYPE.body, 'mt-s3 text-ds-muted')}>
                    {t('medcomSection.exchange_receipt_loading')}
                  </p>
                ) : null}
                {receiptError ? (
                  <div className="mt-s3">
                    <ErrorAlert message={receiptError} />
                  </div>
                ) : null}
              </Modal>
            ) : null}
          </div>
        )}

        {/* ── REF panel — XREF01 / XREF02 / XREF06 ────────────────────────── */}
        {REF_STANDARD_IDS.has(standard) && (
          <div className="mt-8 border-t border-ds-line pt-6">
            <h2 className="text-lg font-semibold">Byg henvisning</h2>
            <p className="mt-1 text-sm text-ds-muted">
              Felterne er forudfyldt med MedCom-eksempelfilens værdier. Dokumentet bygges og
              tjekkes. Det sendes ikke.
            </p>

            <div className="mt-4 grid gap-4">
              {/* ── Kind selector ──────────────────────────────────────────── */}
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Henvisningstype</span>
                <select
                  className="w-full rounded-md border border-ds-field px-3 py-2 text-sm"
                  value={refKind}
                  onChange={(event) => applyRefDefaults(event.target.value as RefKind)}
                >
                  {REF_KINDS.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>

              {/* ── Acknowledgement code ────────────────────────────────────── */}
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Kvitteringskode</span>
                <Input
                  value={refAcknowledgementCode}
                  onChange={(event) => setRefAcknowledgementCode(event.target.value)}
                />
              </label>

              {/* ── Sender ─────────────────────────────────────────────────── */}
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Afsender EAN</span>
                <Input
                  value={refSenderEan}
                  onChange={(event) => setRefSenderEan(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Afsender identifikator</span>
                <Input
                  value={refSenderIdentifier}
                  onChange={(event) => setRefSenderIdentifier(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Afsender identifikatorkode</span>
                <Input
                  value={refSenderIdentifierCode}
                  onChange={(event) => setRefSenderIdentifierCode(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Afsender organisationsnavn</span>
                <Input
                  value={refSenderOrganisationName}
                  onChange={(event) => setRefSenderOrganisationName(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Afsender specialekode</span>
                <Input
                  value={refSenderMedicalSpecialityCode}
                  onChange={(event) => setRefSenderMedicalSpecialityCode(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Henvisers navn</span>
                <Input
                  value={refReferrerPersonName}
                  onChange={(event) => setRefReferrerPersonName(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">
                  Henvisers titel
                  {(refKind === 'XREF02' || refKind === 'XREF06') && (
                    <span className="ml-1 font-normal text-ds-muted">(påkrævet for {refKind})</span>
                  )}
                </span>
                <Input
                  value={refReferrerPersonTitle}
                  onChange={(event) => setRefReferrerPersonTitle(event.target.value)}
                />
              </label>

              {/* ── Receiver ───────────────────────────────────────────────── */}
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Modtager EAN</span>
                <Input
                  value={refReceiverEan}
                  onChange={(event) => setRefReceiverEan(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Modtager identifikator</span>
                <Input
                  value={refReceiverIdentifier}
                  onChange={(event) => setRefReceiverIdentifier(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Modtager identifikatorkode</span>
                <Input
                  value={refReceiverIdentifierCode}
                  onChange={(event) => setRefReceiverIdentifierCode(event.target.value)}
                />
              </label>

              {/* ── Patient ────────────────────────────────────────────────── */}
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Patient CPR</span>
                <Input
                  value={refPatientCpr}
                  onChange={(event) => setRefPatientCpr(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Patient efternavn</span>
                <Input
                  value={refPatientSurname}
                  onChange={(event) => setRefPatientSurname(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Patient fornavn</span>
                <Input
                  value={refPatientGivenName}
                  onChange={(event) => setRefPatientGivenName(event.target.value)}
                />
              </label>

              {/* ── Episode of care (XREF01 / XREF02) ──────────────────────── */}
              {refKind !== 'XREF06' && (
                <>
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium">
                      Forløbsidentifikator — type
                    </span>
                    <Input
                      value={refEpisodeOfCareType}
                      hint="Dannes automatisk, hvis feltet er tomt."
                      onChange={(event) => setRefEpisodeOfCareType(event.target.value)}
                    />
                  </label>
                  <div>
                    <label className="block text-sm">
                      <span className="mb-1 block font-medium">
                        Forløbsidentifikator — identifikator (32 hex-tegn)
                      </span>
                      <Input
                        value={refEpisodeOfCareId}
                        hint="Dannes automatisk, hvis feltet er tomt."
                        onChange={(event) => setRefEpisodeOfCareId(event.target.value)}
                      />
                    </label>
                    <Button
                      type="button"
                      variant="secondary"
                      className="mt-2"
                      onClick={() => setRefEpisodeOfCareId(newEpisodeIdentifier())}
                    >
                      Nyt forløbsID
                    </Button>
                  </div>
                </>
              )}

              {/* ── Episode of care identifier (XREF06 only) ────────────────── */}
              {refKind === 'XREF06' && (
                <div>
                  <label className="block text-sm">
                    <span className="mb-1 block font-medium">
                      Forløbsidentifikator (32 hex-tegn)
                    </span>
                    <Input
                      value={refEpisodeOfCareIdentifier}
                      hint="Dannes automatisk, hvis feltet er tomt."
                      onChange={(event) => setRefEpisodeOfCareIdentifier(event.target.value)}
                    />
                  </label>
                  <Button
                    type="button"
                    variant="secondary"
                    className="mt-2"
                    onClick={() => setRefEpisodeOfCareIdentifier(newEpisodeIdentifier())}
                  >
                    Nyt forløbsID
                  </Button>
                </div>
              )}

              {/* ── Referral ───────────────────────────────────────────────── */}
              {refKind !== 'XREF06' && (
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Diagnosebeskrivelseskode</span>
                  <Input
                    value={refDiagnoseDescriptionCode}
                    onChange={(event) => setRefDiagnoseDescriptionCode(event.target.value)}
                  />
                </label>
              )}

              {refKind === 'XREF06' ? (
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Diagnosekode</span>
                  <select
                    className="w-full rounded-md border border-ds-field px-3 py-2 text-sm"
                    value={refDiagnoseCode}
                    onChange={(event) => setRefDiagnoseCode(event.target.value)}
                  >
                    <option value="">(ingen)</option>
                    {XREF06_DIAGNOSE_CODES.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">Diagnosekode</span>
                  <Input
                    value={refDiagnoseCode}
                    onChange={(event) => setRefDiagnoseCode(event.target.value)}
                  />
                </label>
              )}

              <label className="block text-sm">
                <span className="mb-1 block font-medium">Diagnosetypekode</span>
                <Input
                  value={refDiagnoseTypeCode}
                  onChange={(event) => setRefDiagnoseTypeCode(event.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Diagnosetekst</span>
                <Input
                  value={refDiagnoseText}
                  onChange={(event) => setRefDiagnoseText(event.target.value)}
                />
              </label>

              {refKind !== 'XREF06' && (
                <label className="block text-sm">
                  <span className="mb-1 block font-medium">
                    ClinicalReason (ønsket undersøgelse/behandling/problemstilling)
                  </span>
                  <Textarea
                    rows={3}
                    value={refClinicalReason}
                    hint="Påkrævet. Én linje bliver til én tekstlinje i dokumentet."
                    onChange={(event) => setRefClinicalReason(event.target.value)}
                  />
                </label>
              )}

              {/* ── Anamnesis ──────────────────────────────────────────────── */}
              <label className="block text-sm">
                <span className="mb-1 block font-medium">Anamnese</span>
                <Textarea
                  rows={4}
                  value={refAnamnesis}
                  hint="Én linje bliver til én tekstlinje i dokumentet."
                  onChange={(event) => setRefAnamnesis(event.target.value)}
                />
              </label>

              <div>
                <Button type="button" onClick={() => void onRefCheck()} disabled={refBusy}>
                  <Receipt className="mr-2 h-4 w-4" />
                  {refBusy ? 'Bygger…' : 'Byg henvisning'}
                </Button>
              </div>
            </div>

            {refError && (
              <div className="mt-4">
                <ErrorAlert message={refError} />
              </div>
            )}

            {refPreview && (
              <div className="mt-8">
                <h2 className="text-lg font-semibold">
                  {refPreview.ok ? 'Henvisningen består tjekket' : 'Henvisningen fejler tjekket'}
                </h2>
                <p className="mt-1 text-sm text-ds-muted">Kunde: {refPreview.consumerName}</p>
                <div className="mt-4 flex gap-2">
                  <Button
                    type="button"
                    onClick={() => downloadMedcomDocument(`${refPreview.kind}.xml`, refPreview.xml)}
                  >
                    Hent henvisning
                  </Button>
                </div>
                <ul className="mt-3 space-y-2 text-sm">
                  {refPreview.checks.map((item) => (
                    <li key={item.id}>
                      <span className={item.pass ? 'text-ds-green-ink' : 'text-ds-red-ink'}>
                        {item.pass ? 'Ok' : 'Fejl'}
                      </span>
                      {' — '}
                      {item.detail}
                    </li>
                  ))}
                </ul>
                <pre className="mt-4 max-h-80 overflow-auto rounded-md bg-ds-toast-bg p-4 text-xs text-ds-toast-ink">
                  {refPreview.xml}
                </pre>
              </div>
            )}
          </div>
        )}
      </Card>
      </div>
    </div>
  );
}
